import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BookOpen,
  Image,
  FileText,
  User,
  HelpCircle,
  ExternalLink,
  LogOut,
  Menu,
  Home,
  ChevronRight,
  ScrollText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AreaToggle from '../components/AreaToggle';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';

const memberMenuItems = [
  { path: '/members', label: 'Dashboard', icon: Home },
  { path: '/members/minutes', label: 'Meeting Minutes', icon: FileText },
  { path: '/members/learning', label: 'Learning', icon: BookOpen },
  { path: '/members/collections', label: 'Collections', icon: Image },
  { path: '/members/degree-work', label: 'Degree Work', icon: ScrollText },
  { path: '/members/profile', label: 'Profile', icon: User },
  { path: '/members/support', label: 'Support', icon: HelpCircle },
];

const MenuItem = ({ item, isActive, isMobile, onClose }) => {
  const Icon = item.icon;

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-all duration-200"
        data-testid={`member-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="h-5 w-5" />
        <span>{item.label}</span>
        <ExternalLink className="h-3 w-3 ml-auto" />
      </a>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={isMobile ? onClose : undefined}
      className={`flex items-center gap-3 px-4 py-3 transition-all duration-200 ${isActive
        ? 'border-l-4 border-emerald-500 bg-emerald-50/50 text-emerald-900 font-medium'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
        }`}
      data-testid={`member-menu-${item.path.split('/').pop() || 'dashboard'}`}
    >
      <Icon className="h-5 w-5" />
      <span>{item.label}</span>
    </Link>
  );
};

const Sidebar = ({ isMobile = false, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/members') {
      return location.pathname === '/members';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className={`${isMobile ? 'flex flex-col h-full' : 'h-screen sticky top-0 bg-[#f8f9fa] border-r border-gray-200 hidden md:flex flex-col'}`}>
      {/* Header */}
      <div className="p-6 pb-4">
        <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4" data-testid="back-to-public">
          <ChevronRight className="h-3 w-3 rotate-180" />
          Back to Public Site
        </Link>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Members Area</h2>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto" data-testid="member-sidebar">
        {memberMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            item={item}
            isActive={isActive(item.path)}
            isMobile={isMobile}
            onClose={onClose}
          />
        ))}

        <Separator className="my-4" />

        {/* WebCollect Link - only visible after login */}
        <a
          href="https://webcollect.org.uk/stdavid"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-all duration-200"
          data-testid="member-menu-webcollect"
        >
          <ExternalLink className="h-5 w-5" />
          <span>Membership Login - WebCollect</span>
        </a>
        <a
          href="https://mimir.uk/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-all duration-200"
          data-testid="member-menu-mimir"
        >
          <ExternalLink className="h-5 w-5" />
          <span>Mimir</span>
        </a>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-gray-200 bg-white/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <User className="h-5 w-5 text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate" data-testid="user-display-name">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
};

const MemberLayout = ({ children }) => {
  const { isAuthenticated, isMember, isLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fcfcfc]">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated || !isMember) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#fcfcfc]">
      {/* Header */}
      <header className="bg-[#280a28] text-white" data-testid="member-header">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="member-mobile-menu-toggle">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#f8f9fa]">
                <Sidebar isMobile onClose={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>

            {/* Logo/Title */}
            <div className="text-center flex-1 md:text-left">
              <Link to="/members" className="inline-block" data-testid="member-logo-link">
                <h1 className="font-heading text-lg md:text-xl font-semibold text-emerald-400">
                  St David (University Lodge) No. 36
                </h1>
                <p className="text-xs md:text-sm text-gray-300 mt-0.5">
                  Members Area
                </p>
              </Link>
            </div>

            {/* Area Toggle Button */}
            <div className="flex items-center gap-2">
              <AreaToggle />
              <div className="w-10 md:hidden" />
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
        {/* Sidebar - desktop only */}
        <Sidebar />

        {/* Content */}
        <main className="min-h-[calc(100vh-72px)]" data-testid="member-main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MemberLayout;
